// Small, presentational sub-components reused across the Order flow.
// Extracted from the original monolithic Order.jsx.

import { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { COVER_COLOR_HEX, formatPageRanges, parsePageRanges } from "./pricing";

export const SegBtn = ({ active, onClick, children, testId, disabled, icon: Icon }) => (
  <button
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    data-testid={testId}
    className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-[0.15em] font-bold border transition-all ${
      active
        ? "border-[#0A0A0A] bg-[#059669]/10 text-[#0A0A0A]"
        : disabled
        ? "border-[#E5E5E5] text-[#B0B0B0] cursor-not-allowed opacity-50"
        : "border-[#E5E5E5] text-[#525252] hover:border-[#0A0A0A] hover:text-[#0A0A0A]"
    }`}
  >
    {Icon && <Icon className="w-3.5 h-3.5" />}
    {children}
  </button>
);

export const Toggle = ({ active, onClick, label, testId, icon: Icon, disabled }) => (
  <button
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    data-testid={testId}
    className={`w-full min-w-0 flex items-center justify-between gap-2 px-4 py-3 border text-xs uppercase tracking-[0.15em] font-bold transition-all ${
      active
        ? "border-[#0A0A0A] bg-[#059669]/10"
        : disabled
        ? "border-[#E5E5E5] text-[#B0B0B0] cursor-not-allowed opacity-50 bg-[#FAFAFA]"
        : "border-[#E5E5E5] text-[#525252] hover:border-[#0A0A0A]"
    }`}
  >
    <span className="flex items-center gap-2 min-w-0 flex-1">
      {Icon && <Icon className="w-4 h-4 flex-shrink-0" />}
      <span className="truncate text-left">{label}</span>
    </span>
    <div className={`w-5 h-5 border flex items-center justify-center flex-shrink-0 ${active ? "border-[#059669] bg-[#059669]" : "border-[#E5E5E5]"}`}>
      {active && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
    </div>
  </button>
);

export function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-2">
        {label} {required && <span className="text-[#DC2626]">*</span>}
      </div>
      {children}
    </label>
  );
}

export function CoverPicker({ label, hint, colors, value, onPick, testIdPrefix }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1.5">
        {label}
        <span className="ml-2 normal-case tracking-normal text-[#525252]">{hint}</span>
      </div>
      <div className="flex flex-wrap gap-2" data-testid={testIdPrefix}>
        {colors.map((c) => {
          const active = value === c;
          return (
            <button
              key={c}
              onClick={() => onPick(c)}
              data-testid={`${testIdPrefix}-${c}`}
              className={`flex items-center gap-2 px-3 py-2 border text-xs uppercase tracking-[0.15em] font-bold transition-all ${
                active ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5] hover:border-[#0A0A0A]"
              }`}
            >
              <span
                className="w-4 h-4 border border-[#0A0A0A] inline-block"
                style={{
                  background: COVER_COLOR_HEX[c] || "#FFFFFF",
                  ...(c === "clear" ? {
                    backgroundImage: "linear-gradient(45deg,#E5E5E5 25%,transparent 25%,transparent 75%,#E5E5E5 75%),linear-gradient(45deg,#E5E5E5 25%,transparent 25%,transparent 75%,#E5E5E5 75%)",
                    backgroundSize: "6px 6px",
                    backgroundPosition: "0 0,3px 3px",
                  } : {}),
                }}
              />
              {c}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function EngravingUploader({ idx, current, onUploaded, onClear }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (files) => {
    if (!files?.length) return;
    const f = files[0];
    // Client-side type gate — must be PDF or Word.
    const name = (f.name || "").toLowerCase();
    const okExt = name.endsWith(".pdf") || name.endsWith(".doc") || name.endsWith(".docx");
    if (!okExt) {
      toast.error("Engraving artwork must be a PDF or Word (.doc/.docx) file.");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const { data } = await api.post("/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Enforce exactly 1 page for engraving artwork.
      if (Number(data.pages) !== 1) {
        toast.error(`Engraving artwork must be exactly 1 page — this file has ${data.pages}. Please trim and re-upload.`);
        return;
      }
      onUploaded(data);
      toast.success(`Engraving artwork uploaded · ${data.original_name}`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };
  if (current?.id) {
    return (
      <div className="flex items-center justify-between border border-[#E5E5E5] bg-[#F5F5F5] px-3 py-2" data-testid={`engraving-uploaded-${idx}`}>
        <div className="text-[11px] mono truncate flex-1">{current.name}</div>
        <button
          onClick={onClear}
          data-testid={`engraving-clear-${idx}`}
          className="ml-3 text-[10px] uppercase tracking-[0.2em] font-bold text-[#DC2626] hover:underline"
        >
          Replace
        </button>
      </div>
    );
  }
  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.doc,.docx"
        className="hidden"
        onChange={(e) => upload(Array.from(e.target.files))}
        data-testid={`engraving-file-input-${idx}`}
      />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        data-testid={`engraving-upload-btn-${idx}`}
        className="w-full px-3 py-2 border-2 border-dashed border-[#0A0A0A] text-[11px] uppercase tracking-[0.2em] font-bold hover:bg-[#0A0A0A] hover:text-white disabled:opacity-50"
      >
        {busy ? "Uploading…" : "Choose 1-page artwork (PDF or Word)"}
      </button>
      <div className="text-[10px] text-[#525252] mt-1.5">
        Must be exactly 1 page. Anything longer is rejected.
      </div>
    </div>
  );
}

export function ColorPagesPanel({ idx, file, spec, pricing, onChange }) {
  const mode = spec.color_mode;
  const total = Math.max(1, spec.pages || file.pages || 1);
  const [detectBusy, setDetectBusy] = useState(false);
  const [detectRan, setDetectRan] = useState(false);
  const [rawInput, setRawInput] = useState(() => formatPageRanges(spec.color_pages || []));

  // Auto-run detection the first time Auto is selected for this file.
  useEffect(() => {
    if (mode !== "auto") return;
    if (detectRan) return;
    (async () => {
      setDetectBusy(true);
      try {
        const { data } = await api.post(`/files/${file.id}/detect-color`);
        onChange({ color_pages: data.color_pages || [] });
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail) || "Auto detection failed — use Custom mode.");
        onChange({ color_mode: "bw", color_pages: [] });
      } finally {
        setDetectBusy(false);
        setDetectRan(true);
      }
    })();
  }, [mode, file.id, detectRan]);

  // Live parse of custom input → validated page list on the spec.
  const onCustomChange = (val) => {
    setRawInput(val);
    const parsed = parsePageRanges(val).filter((p) => p >= 1 && p <= total);
    onChange({ color_pages: parsed });
  };

  const colorCount = (spec.color_pages || []).length;
  const bwCount = Math.max(0, total - colorCount);
  const bwRate = Number(pricing?.print?.[spec.paper_size]?.bw ?? 0);
  const colorRate = Number(pricing?.print?.[spec.paper_size]?.color ?? 0);

  return (
    <div className="mt-3 border border-[#E5E5E5] bg-[#F5F5F5] p-3 space-y-2" data-testid={`color-pages-panel-${idx}`}>
      {mode === "auto" ? (
        <>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252]">
              Auto colour detection
            </div>
            <button
              onClick={() => { setDetectRan(false); }}
              disabled={detectBusy}
              data-testid={`color-detect-rerun-${idx}`}
              className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#059669] hover:underline disabled:opacity-50"
            >
              {detectBusy ? "Scanning…" : "Re-scan"}
            </button>
          </div>
          {detectBusy && (
            <div className="text-[11px] text-[#525252] mono">
              Scanning {file.original_name || file.file_name}…
            </div>
          )}
          {!detectBusy && spec.color_pages !== null && (
            <div className="text-[11px] mono text-[#0A0A0A]" data-testid={`color-detect-result-${idx}`}>
              Colour pages found: <span className="font-bold text-[#059669]">{formatPageRanges(spec.color_pages) || "none"}</span>
            </div>
          )}
        </>
      ) : (
        <>
          <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] block">
            Colour pages (e.g. <span className="mono">1, 3-5, 10</span>)
          </label>
          <input
            type="text"
            value={rawInput}
            onChange={(e) => onCustomChange(e.target.value)}
            placeholder={`1-${total}`}
            data-testid={`color-pages-input-${idx}`}
            className="w-full px-3 py-2 border border-[#0A0A0A] mono text-sm bg-white"
          />
        </>
      )}
      <div className="text-[11px] mono flex flex-wrap gap-x-4 gap-y-0.5 pt-1.5 border-t border-[#E5E5E5]">
        <span><b>{colorCount}</b> colour × {colorRate}</span>
        <span><b>{bwCount}</b> B&amp;W × {bwRate}</span>
        <span>= <b>{(colorCount * colorRate + bwCount * bwRate) * Math.max(1, spec.copies || 1)}</b> KES per item</span>
      </div>
    </div>
  );
}

export function PrintPagesSelector({ idx, spec, totalPages, onChange }) {
  const total = Math.max(1, totalPages || spec.pages || 1);
  const [raw, setRaw] = useState(() => formatPageRanges(spec.print_pages || []));
  const printCount = spec.print_all_pages
    ? total
    : parsePageRanges(raw).filter((p) => p >= 1 && p <= total).length;

  const toggle = (checked) => {
    if (checked) {
      onChange({ print_all_pages: true, print_pages: [] });
      setRaw("");
    } else {
      onChange({ print_all_pages: false, print_pages: [] });
    }
  };

  const onInput = (val) => {
    setRaw(val);
    const parsed = parsePageRanges(val).filter((p) => p >= 1 && p <= total);
    onChange({ print_pages: parsed });
  };

  return (
    <div className="mt-2.5">
      <label className="inline-flex items-center gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={!!spec.print_all_pages}
          onChange={(e) => toggle(e.target.checked)}
          data-testid={`print-all-check-${idx}`}
          className="w-4 h-4 accent-[#059669]"
        />
        <span className="text-[11px] uppercase tracking-[0.15em] font-bold text-[#525252]">
          Print all {total} pages
        </span>
      </label>
      {!spec.print_all_pages && (
        <div className="mt-2">
          <input
            type="text"
            value={raw}
            onChange={(e) => onInput(e.target.value)}
            placeholder={`e.g. 1, 3-5, ${total}`}
            data-testid={`print-pages-input-${idx}`}
            className="w-full px-3 py-2 border border-[#0A0A0A] mono text-sm"
          />
          <div className="text-[10px] text-[#525252] mono mt-1">
            {printCount === 0
              ? "Enter which pages to print, else the checkbox above."
              : `${printCount} of ${total} pages selected`}
          </div>
        </div>
      )}
    </div>
  );
}
